import Cookies from "js-cookie";
import { API_URL } from "./url";

let isRefreshing = false;
let refreshSubscribers = [];
const REFRESH_LOCK_KEY = "auth_refresh_lock";
const REFRESH_LOCK_TTL = 10000;
const ACCESS_UPDATE_KEY = "auth_access_update";
const AUTH_DEBUG_KEY = "auth_debug_log";
const REFRESH_RETRY_ATTEMPTS = 3;
const REFRESH_RETRY_BASE_DELAY_MS = 500;
const TAB_ID = Math.random().toString(36).slice(2);

const canUseStorage =
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const AUTH_COOKIE_DOMAIN = ".myhubcc.com";
const AUTH_COOKIE_BASE_OPTIONS = {
  domain: AUTH_COOKIE_DOMAIN,
  path: "/",
  secure: true,
  sameSite: "Lax",
};

export const clearAuthTokens = () => {
  Cookies.remove("access");
  Cookies.remove("refresh");
  Cookies.remove("access_token");
  Cookies.remove("refresh_token");
  Cookies.remove("access", { domain: AUTH_COOKIE_DOMAIN, path: "/" });
  Cookies.remove("refresh", { domain: AUTH_COOKIE_DOMAIN, path: "/" });
  Cookies.remove("access_token", { domain: AUTH_COOKIE_DOMAIN, path: "/" });
  Cookies.remove("refresh_token", { domain: AUTH_COOKIE_DOMAIN, path: "/" });

  if (canUseStorage) {
    window.localStorage.removeItem("access");
    window.localStorage.removeItem("refresh");
    window.localStorage.removeItem("access_token");
    window.localStorage.removeItem("refresh_token");
  }
};

export const setAuthTokens = (access, refresh) => {
  clearAuthTokens();

  if (access) {
    Cookies.set("access", access, {
      ...AUTH_COOKIE_BASE_OPTIONS,
      expires: 1,
    });
    if (canUseStorage) {
      window.localStorage.setItem("access", access);
    }
  }

  if (refresh) {
    Cookies.set("refresh", refresh, {
      ...AUTH_COOKIE_BASE_OPTIONS,
      expires: 7,
    });
    if (canUseStorage) {
      window.localStorage.setItem("refresh", refresh);
    }
  }
};


const getAccessToken = () =>
  (canUseStorage ? window.localStorage.getItem("access") : null) ||
  Cookies.get("access") ||
  Cookies.get("access_token") ||
  (canUseStorage ? window.localStorage.getItem("access_token") : null) ||
  null;

const getRefreshToken = () =>
  (canUseStorage ? window.localStorage.getItem("refresh") : null) ||
  Cookies.get("refresh") ||
  Cookies.get("refresh_token") ||
  (canUseStorage ? window.localStorage.getItem("refresh_token") : null) ||
  null;

const setAccessToken = (token) => {
  if (!token) return;
  Cookies.set("access", token, {
    ...AUTH_COOKIE_BASE_OPTIONS,
    expires: 1,
  });
  if (canUseStorage) {
    window.localStorage.setItem("access", token);
  }
};

const setRefreshToken = (token) => {
  if (!token) return;
  Cookies.set("refresh", token, {
    ...AUTH_COOKIE_BASE_OPTIONS,
    expires: 7,
  });
  if (canUseStorage) {
    window.localStorage.setItem("refresh", token);
  }
};

const logAuthEvent = (event, payload = {}) => {
  if (!canUseStorage) return;
  try {
    const raw = window.localStorage.getItem(AUTH_DEBUG_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const next = Array.isArray(list) ? list : [];
    next.push({
      ts: new Date().toISOString(),
      tab: TAB_ID,
      event,
      ...payload,
    });
    // keep recent 100 entries
    window.localStorage.setItem(
      AUTH_DEBUG_KEY,
      JSON.stringify(next.slice(-100))
    );
  } catch {
    // ignore debug log errors
  }
};

const readRefreshLock = () => {
  if (!canUseStorage) return null;
  const raw = window.localStorage.getItem(REFRESH_LOCK_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const acquireRefreshLock = () => {
  if (!canUseStorage) return true;
  const existing = readRefreshLock();
  const now = Date.now();
  if (existing && now - Number(existing.ts || 0) < REFRESH_LOCK_TTL) {
    return false;
  }
  const payload = { ts: now, tab: TAB_ID };
  window.localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(payload));
  const confirm = readRefreshLock();
  return confirm?.tab === TAB_ID;
};

const releaseRefreshLock = () => {
  if (!canUseStorage) return;
  const existing = readRefreshLock();
  if (!existing || existing.tab !== TAB_ID) return;
  window.localStorage.removeItem(REFRESH_LOCK_KEY);
};

const notifyAccessUpdate = (token) => {
  if (!canUseStorage) return;
  window.localStorage.setItem(
    ACCESS_UPDATE_KEY,
    JSON.stringify({ token, ts: Date.now(), tab: TAB_ID })
  );
};

const waitForAccessToken = (timeoutMs = REFRESH_LOCK_TTL) =>
  new Promise((resolve) => {
    if (!canUseStorage) {
      resolve(getAccessToken());
      return;
    }
    const started = Date.now();
    let timeoutId;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("storage", onStorage);
    };

    const check = () => {
      const access = getAccessToken();
      if (access) {
        cleanup();
        resolve(access);
        return;
      }
      const lock = readRefreshLock();
      if (!lock || Date.now() - started > timeoutMs) {
        cleanup();
        resolve(null);
        return;
      }
      timeoutId = setTimeout(check, 250);
    };

    const onStorage = (event) => {
      if (
        event.key === ACCESS_UPDATE_KEY ||
        event.key === REFRESH_LOCK_KEY
      ) {
        check();
      }
    };

    window.addEventListener("storage", onStorage);
    check();
  });

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const shouldRetryRefreshStatus = (status) => status >= 500;
const isRefreshRejectedStatus = (status) =>
  status === 400 || status === 401 || status === 403;

const createAuthTransientError = (message) => {
  const err = new Error(message);
  err.isAuthTransient = true;
  return err;
};

const subscribeTokenRefresh = (resolve, reject) => {
  refreshSubscribers.push({ resolve, reject });
};

const onRefreshed = (newAccessToken) => {
  refreshSubscribers.forEach(({ resolve }) => resolve(newAccessToken));
  refreshSubscribers = [];
};

const onRefreshFailed = (error) => {
  refreshSubscribers.forEach(({ reject }) => reject(error));
  refreshSubscribers = [];
};

export const logout = () => {
  logAuthEvent("logout");
  clearAuthTokens();
  if (canUseStorage) {
    window.localStorage.removeItem(REFRESH_LOCK_KEY);
  }
  window.location.href = "/login";
};

const fetchWithAuth = async (url, options = {}, { onTokenUpdate } = {}) => {
  const access = getAccessToken();
  const refresh = getRefreshToken();

  const stringifiedBody =
    options.body && typeof options.body !== "string"
      ? JSON.stringify(options.body)
      : options.body;

  const applyToken = (token) => {
    const isFormData = options.body instanceof FormData;

    return {
      ...options,
      method: options.method || "GET",
      body: isFormData ? options.body : stringifiedBody,
      headers: {
        ...(!isFormData && stringifiedBody
          ? { "Content-Type": "application/json" }
          : {}),
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    };
  };
  const retryRequest = async (token) => {
    return await fetch(url, applyToken(token));
  };

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh(
        async (newAccessToken) => {
          try {
            const retryResponse = await retryRequest(newAccessToken);
            resolve(retryResponse);
          } catch (err) {
            reject(err);
          }
        },
        (err) => reject(err)
      );
    });
  }

  let response;
  if (access) {
    response = await retryRequest(access);
    if (response.status !== 401) return response;
  }

  if (!refresh) {
    logAuthEvent("logout_reason", { reason: "no_refresh_token", url });
    logout();
    throw new Error("Unauthorized: No refresh token");
  }

  if (!acquireRefreshLock()) {
    logAuthEvent("refresh_wait_lock", { url });
    const tokenFromOtherTab = await waitForAccessToken();
    if (tokenFromOtherTab) {
      logAuthEvent("refresh_token_received_from_other_tab", { url });
      return await retryRequest(tokenFromOtherTab);
    }
    if (!acquireRefreshLock()) {
      logAuthEvent("logout_reason", { reason: "refresh_lock_timeout", url });
      logout();
      throw new Error("Token refresh locked");
    }
  }

  isRefreshing = true;
  logAuthEvent("refresh_start", { url });

  try {
    let refreshResponse = null;
    let refreshNetworkError = null;

    for (
      let attempt = 1;
      attempt <= REFRESH_RETRY_ATTEMPTS;
      attempt += 1
    ) {
      try {
        refreshResponse = await fetch(`${API_URL}/user/token/refresh/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh }),
        });
        refreshNetworkError = null;

        if (
          refreshResponse.ok ||
          isRefreshRejectedStatus(refreshResponse.status) ||
          !shouldRetryRefreshStatus(refreshResponse.status) ||
          attempt === REFRESH_RETRY_ATTEMPTS
        ) {
          break;
        }

        const delayMs = REFRESH_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
        logAuthEvent("refresh_retry_status", {
          url,
          attempt,
          status: refreshResponse.status,
          delayMs,
        });
        await sleep(delayMs);
      } catch (err) {
        refreshNetworkError = err;
        if (attempt === REFRESH_RETRY_ATTEMPTS) {
          break;
        }
        const delayMs = REFRESH_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
        logAuthEvent("refresh_retry_network", {
          url,
          attempt,
          delayMs,
          message: String(err?.message || err),
        });
        await sleep(delayMs);
      }
    }

    if (refreshNetworkError && !refreshResponse) {
      throw createAuthTransientError("Temporary auth refresh network error");
    }

    if (!refreshResponse.ok) {
      logAuthEvent("refresh_failed", {
        status: refreshResponse.status,
        url,
      });
      if (isRefreshRejectedStatus(refreshResponse.status)) {
        logAuthEvent("logout_reason", {
          reason: "refresh_rejected",
          status: refreshResponse.status,
          url,
        });
        logout();
        throw new Error("Session expired");
      }

      if (shouldRetryRefreshStatus(refreshResponse.status)) {
        throw createAuthTransientError("Temporary auth refresh server error");
      }

      throw createAuthTransientError("Temporary auth refresh failed");
    }

    const { access: newAccessToken, refresh: newRefreshToken } =
      await refreshResponse.json();

    if (!newAccessToken) {
      logAuthEvent("refresh_failed", { reason: "no_access_in_response", url });
      logout();
      throw new Error("No new access token returned");
    }

    setAccessToken(newAccessToken);
    if (newRefreshToken) {
      setRefreshToken(newRefreshToken);
    }
    onTokenUpdate?.(newAccessToken);

    onRefreshed(newAccessToken);
    notifyAccessUpdate(newAccessToken);
    logAuthEvent("refresh_success", { url });

    return await retryRequest(newAccessToken);
  } catch (err) {
    onRefreshFailed(err);
    logAuthEvent("refresh_exception", { message: String(err?.message || err), url });
    throw err;
  } finally {
    isRefreshing = false;
    releaseRefreshLock();
  }
};

export default fetchWithAuth;
